import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import { type LoaderFunction, Navigate, Outlet, type RouteObject } from 'react-router-dom'

import track from '@app/lib/analyticsHelper'
import AddBasicCardDrawer from '@app/pages/strategies/components/addCards/addBasicCardDrawer'
import AddCardDrawer from '@app/pages/strategies/components/addCards/addCardDrawer'
import AddMetricCardDrawer from '@app/pages/strategies/components/addCards/addMetricCardDrawer'
import AddPlaybookDrawer from '@app/pages/strategies/components/addPlaybooks/addPlaybookDrawer'
import EdgeDrawer from '@app/pages/strategies/components/edgeDrawer/edgeDrawer'
import PreviewModal from '@app/pages/strategies/components/map/previewModal'
import StrategySettings from '@app/pages/strategies/components/settings/strategySettings'
import StrategyDrawer from '@app/pages/strategies/components/strategyDrawer'
import DrawerShow from '@app/pages/strategies/drawerShow'
import Map from '@app/pages/strategies/map'
import Show from '@app/pages/strategies/show'
import Tabs from '@app/pages/strategies/tabs'
import StrategyDetails from '@app/pages/strategies/tabs/strategyDetails'
import AiAssistantRoutes from '@app/routes/aiAssistants'
import AIDashboardRoutes from '@app/routes/aiDashboard'
import { basicCardShowTabRoutes } from '@app/routes/basicCards'
import ChangelogRoutes from '@app/routes/changelogs'
import { entityShowTabRoutes, workShowTabRoutes } from '@app/routes/events'
import { mapRoutes as KeyResultRoutes } from '@app/routes/goals'
import { metricShowTabRoutes } from '@app/routes/metrics'
import ReportRoutes from '@app/routes/reports'
import ErrorPage from '@app/shared/errorPage'
import extractOrderings from '@app/shared/utils/extractOrderings'
import { useStore } from '@app/store'
import unpack from '@app/store/unpack'
import { requiresAuthorization } from '@app/utils/auth'
import { CardTypes } from '@graphql/documents/card_types.graphql'
import { Sources } from '@graphql/documents/integration.graphql'
import { Playbooks } from '@graphql/documents/playbook.graphql'
import { Strategies, Strategy } from '@graphql/documents/strategy.graphql'
import type { StrategiesQuery, StrategiesQueryVariables, StrategyQuery, StrategyQueryVariables } from '@graphql/queries'
import { PipelineEnum } from '@graphql/types'

const setupMapData = (strategy) => {
  const { bulkAdd, setFilteredNodeIds } = useStore.getState()

  if (!strategy) {
    throw { statusText: 'Not found', status: 404, internal: false, data: null }
  }

  const combinedArray = unpack(strategy)
  bulkAdd(combinedArray)

  const filteredNodeIds = strategy.nodes?.map((node) => node.id)
  setFilteredNodeIds(() => filteredNodeIds)
}

const loadMap: LoaderFunction = async ({ params }) => {
  const { strategyId } = params
  const { loaderQuery, currentUser } = useStore.getState()

  // Do not `await` this. The result will be handled by the exchange.
  // TODO: put this in an organization-level loader?
  loaderQuery(CardTypes)

  const resp = await loaderQuery<StrategyQuery, StrategyQueryVariables>(Strategy, { id: strategyId })
  const strategy = resp?.data?.strategy

  setupMapData(strategy)

  track(
    'strategy:loaded',
    {
      strategy_id: strategy.id
    },
    currentUser?.user
  )

  return { strategy }
}

const loadEmbeddedMap = async ({ params }) => {
  const { strategyId } = params
  const { loaderQuery, currentUser } = useStore.getState()

  loaderQuery(CardTypes)

  const resp = await loaderQuery<StrategyQuery, StrategyQueryVariables>(Strategy, {
    id: strategyId,
    loadAsAnonymous: true
  })
  const strategy = resp?.data?.strategy

  setupMapData(strategy)

  track(
    'strategy:embed:loaded',
    {
      strategy_id: strategy.id
    },
    currentUser?.user
  )

  return { strategy }
}

const loadNodeMap = async ({ params }) => {
  const { nodeId: strategyId } = params
  const { loaderQuery } = useStore.getState()

  const resp = await loaderQuery<StrategyQuery, StrategyQueryVariables>(Strategy, { id: strategyId })
  const strategy = resp?.data?.strategy

  return { strategy }
}

const loadMaps = async ({ request }) => {
  const url = new URL(request.url)
  const page = parseInt(url.searchParams.get('page'), 10) || 1
  const limit = parseInt(url.searchParams.get('limit'), 10) || null
  const filter = url.searchParams.get('filter')
  const order = extractOrderings(url.searchParams)
  const { loaderQuery } = useStore.getState()

  const variables: StrategiesQueryVariables = omitBy({ page, limit, filter, order }, isNull)

  const result = await loaderQuery<StrategiesQuery, StrategiesQueryVariables>(Strategies, variables)

  const { addObjectPage } = useStore.getState()

  const { collection = [], metadata } = result.data.strategies
  addObjectPage('strategy', collection, metadata)

  return result.data
}

const loadIntegrationInfo = async () => {
  const { loaderQuery } = useStore.getState()
  const result = await loaderQuery(Sources)
  return { integrations: result.data.sources }
}

const loadPlaybooks = async () => {
  await requiresAuthorization('update', 'strategy')
  const { loaderQuery } = useStore.getState()
  const result = await loaderQuery(Playbooks)

  return { playbooks: result.data.playbooks }
}

const routes: RouteObject = {
  path: 'strategy',
  element: <Outlet />,
  children: [
    {
      index: true,
      loader: loadMaps,
      element: <Navigate to="/maps" replace />,
      handle: {
        headData: () => ({ title: 'All maps' })
      }
    },
    {
      path: 'embed/:strategyId',
      element: <Show embedded />,
      errorElement: <ErrorPage isEmbedded />,
      loader: loadEmbeddedMap,
      children: [
        {
          index: true,
          element: <Navigate to="map" replace />
        },
        {
          path: 'map',
          element: <Map />
        }
      ]
    },
    {
      path: ':strategyId',
      element: <Show />,
      loader: loadMap,
      children: [
        {
          index: true,
          element: <Navigate to="map" replace />
        },
        {
          path: 'map',
          element: <Map />,
          children: [
            ReportRoutes,
            {
              path: 'edges/:edgeId',
              element: <EdgeDrawer />
            },
            {
              path: 'settings',
              element: <StrategySettings />
            },
            {
              path: 'add',
              children: [
                {
                  path: 'playbook',
                  loader: loadPlaybooks,
                  element: <AddPlaybookDrawer />
                },
                {
                  path: 'basicCard/:cardTypeId?',
                  loader: loadIntegrationInfo,
                  element: <AddBasicCardDrawer />
                },
                {
                  path: 'metric',
                  loader: loadIntegrationInfo,
                  element: <AddMetricCardDrawer />
                },
                {
                  path: ':type',
                  loader: loadIntegrationInfo,
                  element: <AddCardDrawer />
                }
              ]
            },
            {
              path: 'metric',
              loader: () => ({
                nodeType: 'metric'
              }),
              element: <StrategyDrawer />,
              children: metricShowTabRoutes('strategy')
            },
            {
              path: 'entity',
              loader: () => ({
                nodeType: 'entity'
              }),
              element: <StrategyDrawer />,
              children: entityShowTabRoutes('strategy')
            },
            {
              path: 'work',
              loader: () => ({
                nodeType: 'work'
              }),
              element: <StrategyDrawer />,
              children: workShowTabRoutes('strategy')
            },
            {
              path: 'basicCard',
              loader: () => ({
                nodeType: 'basicCard'
              }),
              element: <StrategyDrawer />,
              children: basicCardShowTabRoutes('strategy')
            },
            {
              path: 'strategy',
              loader: () => ({
                nodeType: 'strategy'
              }),
              element: <StrategyDrawer />,
              children: [
                {
                  path: ':nodeId',
                  children: [
                    {
                      path: '*',
                      id: 'strategy-strategy',
                      loader: loadNodeMap,
                      element: <DrawerShow />,
                      children: [
                        {
                          index: true,
                          element: <Navigate to="details" replace />
                        },
                        {
                          element: <Tabs />,
                          children: [{ path: 'details', element: <StrategyDetails /> }]
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            AiAssistantRoutes('ai', PipelineEnum.MetricContext),
            {
              path: 'preview/:submapId',
              element: <PreviewModal />
            }
          ]
        },
        ChangelogRoutes,
        ReportRoutes,
        KeyResultRoutes,
        AIDashboardRoutes
      ]
    }
  ]
}

export default routes
